import {
    DownloadButtonData,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { JobStatusType, Licenses } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectSelectedLayerIds } from "panels/content/side-panel/slice.new"
import { selectVideoEvents, selectVideoJobByFlightId } from "./VideoSlice"
import { selectVideoFlightById } from "features/entity-caching/flights/selectors"
import { stageKml } from "features/modals/ExportLayers/module"
import { downloadUriSameOrigin } from "tools/browser/download"
import { createFlightpathKML } from "@nova/data-create-flight-path-kml"
import { createFMVFileUrl } from "./module"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { decode } from "@msgpack/msgpack"
import { VideoMetadata } from "@nova/slice-video"

export const useDownloadButtonData = (): DownloadButtonData => {
    const selectedLayerId = useAppSelector(selectSelectedLayerIds).at(0)
    const id = selectedLayerId || ""
    const job = useAppSelector(selectVideoJobByFlightId(id))
    const flight = useAppSelector(selectVideoFlightById(id))
    const localMetadata = useAppSelector(selectVideoEvents(id))

    if (job?.status !== JobStatusType.COMPLETED) return { exportOptions: {} }
    const jobIsCompleted = job?.status === JobStatusType.COMPLETED

    const downloadButtonData: NestedDownloadData = {}

    const videoOptions: NestedDownloadData = {}

    if (jobIsCompleted) {
        const downloadVideo = async () => {
            const s3Key = !!flight?.demoId ? `public/sample-data/${job.videoPath}` : job.videoPath
            const url = await createFMVFileUrl(s3Key, !!flight?.demoId)

            window.open(url, "_blank")
        }

        videoOptions.MP4 = { callback: downloadVideo, restrictedLicenses: [Licenses.Lite] }
    }

    const flightPathOptions: NestedDownloadData = {}

    if (jobIsCompleted && job.srtPath) {
        const downloadSRT = async () => {
            const s3Key = !!flight?.demoId ? `public/sample-data/${job.srtPath!}` : job.srtPath!
            const url = await createFMVFileUrl(s3Key, !!flight?.demoId)

            window.open(url, "_blank")
        }

        flightPathOptions.SRT = { callback: downloadSRT, restrictedLicenses: [Licenses.Lite] }
    }

    if (flight && (localMetadata || flight.job?.metadataPath)) {
        const downloadFlightPathKML = async () => {
            let metadata = localMetadata
            if (!metadata) {
                const metadataPath = flight.job!.metadataPath!
                const s3Key = !!flight?.demoId ? `public/sample-data/${metadataPath}` : metadataPath
                const metadataBuffer = await (
                    await fileManagementApi.fetchFile(s3Key, { isDemo: !!flight?.demoId })
                ).arrayBuffer()
                metadata = decode(metadataBuffer) as VideoMetadata
            }
            const flightpath = metadata?.map((frame) => ({
                longitude: frame[0],
                latitude: frame[1],
                altitude: frame[2],
            }))
            const kml = createFlightpathKML(flight.name + " Flightpath", [flightpath], false)

            const files: { blob: Blob; name: string }[] = []
            stageKml(kml, flight.name, files)

            const url = URL.createObjectURL(files[0].blob)
            downloadUriSameOrigin(files[0].name, url)
        }

        flightPathOptions.KML = {
            callback: downloadFlightPathKML,
            restrictedLicenses: [Licenses.Lite],
        }
    }

    if (Object.keys(videoOptions).length > 0) downloadButtonData.Video = videoOptions
    if (Object.keys(flightPathOptions).length > 0)
        downloadButtonData["Flight Path"] = flightPathOptions

    return { exportOptions: downloadButtonData }
}
